import React from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import './NoAccess.css';

// Shown when Firebase authenticated someone but the app won't let them in:
// either nobody has added their email yet (access is invite-only), their access
// was turned off, or they've reached a section their profile doesn't include.
//
// The wording separates "you're not set up" from "something broke", because the
// fix is different: one needs an admin, the other needs us.
// `code`: 'no-account' | 'inactive' from the server, 'unavailable' when the
// access check itself failed (a network error or a server that's down), or
// absent for the ordinary "your profile doesn't include this" case.
export default function NoAccess({ code, message, title, children, onRetry }) {
    const heading = title || {
        inactive: 'Your access has been turned off',
        'no-account': "You're not set up yet",
        unavailable: "We couldn't check your access",
    }[code] || "You don't have access to this page";

    const body = children || {
        inactive: 'An admin has deactivated this account. Ask them to turn it back on if you still need access.',
        'no-account': 'Your sign-in worked, but nobody has added your email to this app yet. Ask an admin to add you and assign a profile, then sign in again.',
        // Not a permissions problem at all, so it mustn't read like one.
        unavailable: 'Something went wrong loading your permissions — this is usually a connection problem, not a permissions one. Try again in a moment.',
    }[code] || "Your profile doesn't include this section. If you need it, ask an admin to add it to your profile.";

    return (
        <div className="no-access">
            <div className="no-access__card">
                <h1 className="no-access__title">{heading}</h1>
                <p className="no-access__text">{body}</p>
                {/* The raw error, which only helps in the unavailable case —
                    elsewhere it just repeats the sentence above. */}
                {message && code === 'unavailable' && <p className="no-access__detail">{message}</p>}
                <div className="no-access__actions">
                    {onRetry && (
                        <button type="button" className="blueButton" onClick={onRetry}>Try Again</button>
                    )}
                    <button className="signOutButton" onClick={() => signOut(auth)}>Sign Out</button>
                </div>
            </div>
        </div>
    );
}
